import styled, { keyframes } from "styled-components";

interface IWrapper {
  $isMapActive: boolean;
  $isListEmpty: boolean;
}

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

export const ListContainer = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 1;
  box-sizing: border-box;
  overflow-x: hidden;
  padding: 8px;
`;

export const Wrapper = styled.div<IWrapper>`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: grid;
  margin-top: 0;
  gap: 12px;
  box-sizing: border-box;

  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: ${({ $isListEmpty }) =>
      $isListEmpty ? "1fr" : "repeat(2, minmax(0, 1fr))"};
    gap: 12px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 14px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    gap: 16px;

    grid-template-columns: ${({ $isListEmpty }) =>
      $isListEmpty ? "1fr" : "repeat(3, minmax(0, 1fr))"};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    gap: 18px;

    ${({ $isMapActive, $isListEmpty }) =>
      $isMapActive &&
      !$isListEmpty &&
      `
        grid-template-columns: repeat(3, minmax(0, 1fr));
        max-height: 700px;
        overflow-y: auto;
      `}

    ${({ $isMapActive, $isListEmpty }) =>
      !$isMapActive &&
      !$isListEmpty &&
      `
        grid-template-columns: repeat(4, minmax(0, 1fr));
      `}

    ${({ $isListEmpty }) =>
      $isListEmpty &&
      `
        grid-template-columns: 1fr;
      `}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktopXl}) {
    gap: 20px;

    ${({ $isMapActive, $isListEmpty }) =>
      $isMapActive &&
      !$isListEmpty &&
      `
        grid-template-columns: repeat(3, minmax(0, 1fr));
      `}

    ${({ $isMapActive, $isListEmpty }) =>
      !$isMapActive &&
      !$isListEmpty &&
      `
        grid-template-columns: repeat(5, minmax(0, 1fr));
      `}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktopTxl}) {
    gap: 22px;

    ${({ $isMapActive, $isListEmpty }) =>
      $isMapActive &&
      !$isListEmpty &&
      `
        grid-template-columns: repeat(3, minmax(0, 1fr));
      `}

    ${({ $isMapActive, $isListEmpty }) =>
      !$isMapActive &&
      !$isListEmpty &&
      `
        grid-template-columns: repeat(5, minmax(0, 1fr));
      `}
  }
`;

export const FadeIn = styled.div`
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  & > a {
    display: block;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  [data-testid="property-card"] {
    min-width: 0;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  .property-title-newcomers,
  .property-address-newcomers {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .property-details-wrapper-newcomers {
    min-width: 0;
    flex-wrap: nowrap;
    gap: 2px;
  }

  .property-detail-newcomers {
    white-space: nowrap;
  }

  .cover-image-container {
    min-width: 0;
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    background-color: ${({ theme }) => theme.palette.greyscale.grey200};

    > div {
      height: 100%;
      min-height: 0;
    }
  }

  .cover-image-container img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .cover-image-container .slick-list {
    height: 100%;
    overflow: hidden;
  }

  .cover-image-container .slick-track {
    display: flex !important;
    height: 100%;
    width: auto !important;
    max-width: none !important;
  }

  .cover-image-container .slick-slide {
    height: 100%;
    flex-shrink: 0;

    > div {
      height: 100%;
    }
  }

  .property-info-container-newcomers {
    min-width: 0;
  }

  -webkit-animation: ${fadeIn} 1s;
  -moz-animation: ${fadeIn} 1s;
  -ms-animation: ${fadeIn} 1s;
  -o-animation: ${fadeIn} 1s;
  animation: ${fadeIn} 1s;
`;

export const EmptyListMessage = styled.div`
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
  padding: 16px;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: flex-start;
    padding: 24px;
  }
`;

export const PropertiesNotFountTitle = styled.h6`
  font-size: ${24 / 16}rem;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: hsla(210, 30%, 28%, 0.87);
  margin: 0 0 16px 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    font-size: ${20 / 16}rem;
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    font-size: ${28 / 16}rem;
  }
`;

export const PropertiesNotFountImage = styled.img`
  margin-top: 8px;
  max-width: 100%;
  height: auto;
  align-self: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    margin-top: 16px;
    max-width: 90%;
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: 24px;
    max-width: 80%;
    align-self: flex-start;
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    max-width: 600px;
  }
`;

export const EmptyAreaContainer = styled.div`
  background-color: ${({ theme }) => theme.palette.primary.blue50};
  border: 1px solid ${({ theme }) => theme.palette.primary.blue100};
  border-radius: 8px;
  padding: 16px;
  margin: 0;
  min-height: 140px;
  color: ${({ theme }) => theme.palette.primary.blue600};
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  grid-column: 1 / -1;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    padding: 20px;
    min-height: 160px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 24px;
    min-height: 180px;
  }
`;

export const EmptyAreaContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    flex-direction: row;
    justify-content: center;
    text-align: left;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 16px;
    margin-bottom: 20px;
  }
`;

export const EmptyAreaIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.primary.blue100};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.primary.blue500};
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    width: 24px;
    height: 24px;
    font-size: 16px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 28px;
    height: 28px;
    font-size: 18px;
  }
`;

export const EmptyAreaTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.primary.blue800};
  line-height: 1.3;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    font-size: 15px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 16px;
    margin-bottom: 6px;
  }
`;

export const EmptyAreaMessage = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.primary.blue700};

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    font-size: 13px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 14px;
  }
`;

export const EmptyAreaActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 16px;
    margin-top: 20px;
  }
`;

export const ReloadButton = styled.button`
  background-color: ${({ theme }) => theme.palette.primary.blue500};
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: white;
  transition: all 0.2s ease;
  flex: 1;
  max-width: 180px;
  min-height: 36px;

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.blue600};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.primary.blue700};
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.primary.blue300};
    outline-offset: 2px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    padding: 10px 20px;
    font-size: 14px;
    max-width: 200px;
    min-height: 40px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 12px 24px;
    font-size: 15px;
    max-width: 220px;
    min-height: 44px;
  }
`;

export const ErrorStateContainer = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  gap: 12px;
`;

export const ErrorStateTitle = styled.h3`
  margin: 0;
  font-size: ${20 / 16}rem;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: hsla(210, 30%, 28%, 0.87);
`;

export const ErrorStateMessage = styled.p`
  margin: 0;
  font-size: ${14 / 16}rem;
  color: ${({ theme }) => theme.palette.greyscale.grey600};
`;